import React, { useEffect, useMemo, useState } from 'react'

import './tracking.css'
import { Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, InputLabel, ListItemText, MenuItem, Select, Switch, Tab, Tabs, Typography, } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import 'dayjs/locale/es';

import { timeToSeconds, calculateTimeDifference, getAngleDifference, isDirectionalRoutePoint, mixedNaturalSort, TabPanel } from '../../utils/generalFunctions';
import EventsTab from './reportsTabs/eventsTab';
import ScheduleTab from './reportsTabs/scheduleTab';
import StatsTab from './reportsTabs/statsTab';
import BootstrapTooltip from '../../components/tooltip';

dayjs.locale('es');

// Implementar una nueva condición para avanzar en la ruta al ordenar los eventos, si se avanzan 5 puntos de control sin encontrar coincidencia con eventos, reiniciar el orden de los eventos.

const getCompensationSeconds = (value) => {
    const rawTime = typeof value === 'string' ? value : value?.time;

    if (!rawTime) return 0;

    const [minutes = 0, seconds = 0] = rawTime.split(':').map(Number);

    return ((minutes || 0) * 60) + (seconds || 0);
};

const normalizeCompensatedSeconds = (seconds) => {
    const daySeconds = 86400;

    return ((seconds % daySeconds) + daySeconds) % daySeconds;
};

const normalizeReportKey = (value = '') => {
    return String(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
};

const getReportCellText = (cell) => {
    if (cell == null) return '';
    if (typeof cell !== 'object') return String(cell);

    return String(cell.v ?? cell.value ?? cell.label ?? cell.text ?? cell.raw ?? '');
};

const parseReportDate = (cell) => {
    if (!cell) return null;

    const raw = typeof cell === 'object' ? cell.raw : cell;

    if (typeof raw === 'number') {
        const timestamp = raw > 100000000000 ? raw : raw > 1000000000 ? raw * 1000 : null;

        if (!timestamp) return null;

        const date = new Date(timestamp);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof raw === 'string') {
        const date = new Date(raw);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const value = getReportCellText(cell);
    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
};

const cleanCheckpointLabel = (value) => {
    return getReportCellText(value)
        .replaceAll('Salió de ', '')
        .replaceAll('SaliÃ³ de ', '')
        .replaceAll('Salida de ', '')
        .replace(/\s*:\s*\d+\s*$/, '')
        .trim();
};

const getReportCellByKeywords = (row, section, keywords) => {
    const normalizedKeywords = keywords.map(normalizeReportKey);

    const matchingColumn = section?.columns?.find(column => {
        const field = normalizeReportKey(column.field);
        const title = normalizeReportKey(column.title);

        return normalizedKeywords.some(keyword => field.includes(keyword) || title.includes(keyword));
    });

    if (matchingColumn && row[matchingColumn.field] != null) {
        return row[matchingColumn.field];
    }

    const matchingKey = Object.keys(row).find(key =>
        normalizedKeywords.some(keyword => normalizeReportKey(key).includes(keyword))
    );

    return matchingKey ? row[matchingKey] : null;
};

const getReportCellRaw = (cell) => {
    return cell && typeof cell === 'object' ? cell.raw : null;
};

const getReportRows = (section) => {
    return section.data?.flatMap(dataItem =>
        dataItem.rows?.map(row => ({ row, dataItem })) || []
    ) || [];
};

const Reports = ({ devices, userSchedules, userRoute, generateReport, generateOrientationsReport, defaultEventRows, setDefaultEventRows, rows, setRows, showAlert, toleranceTime, compensationTime }) => {

    const [startDate, setStartDate] = useState(dayjs().startOf('day')); // Fecha de inicio
    const [startTime, setStartTime] = useState(dayjs().startOf('day')); // Hora de inicio
    const [endDate, setEndDate] = useState(dayjs().endOf('day')); // Fecha de fin
    const [endTime, setEndTime] = useState(dayjs().endOf('day')); // Hora de fin

    const [loadReport, setLoadReport] = useState(false);

    const [tabIndex, setTabIndex] = useState(0);

    const [vehicleFilter, setVehicleFilter] = useState('');
    const [checkpointFilter, setCheckpointFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [order, setOrder] = useState('init'); // Orden actual: 'init', 'asc', 'desc'
    const [orderBy, setOrderBy] = useState(null); // Columna para ordenar

    const [selectedSchedule, setSelectedSchedule] = useState({});

    const [selectedVehicles, setSelectedVehicles] = useState(['all']);

    const [vehicleSchedules, setVehicleSchedules] = useState({});
    const [openScheduleModal, setOpenScheduleModal] = useState(false);
    const [applyCompensationToReport, setApplyCompensationToReport] = useState(false);
    const [reportSchedules, setReportSchedules] = useState(userSchedules);

    const compensatedSchedules = useMemo(() => {
        const seconds = getCompensationSeconds(compensationTime);
        const type = typeof compensationTime === 'string' ? 'late' : compensationTime?.type;
        const offsetSeconds = type === 'ahead' ? seconds : -seconds;

        if (!offsetSeconds) return userSchedules;

        return userSchedules.map(schedule => ({
            ...schedule,
            turns: schedule.turns?.map(turn =>
                turn.map(point => ({
                    ...point,
                    time: point.time == null
                        ? point.time
                        : normalizeCompensatedSeconds(point.time + offsetSeconds)
                }))
            ) || []
        }));
    }, [userSchedules, compensationTime]);

    useEffect(() => {
        if (rows.length === 0) {
            setReportSchedules(userSchedules);
        }
    }, [userSchedules, rows.length]);

    useEffect(() => {
        if (userSchedules.length === 0 || Object.keys(vehicleSchedules).length === 0) return;

        const filteredDevices = devices.filter(device => defaultEventRows.some(row => row.vehicle === device.label));

        const firstVehicleWithSchedule = filteredDevices.find(device => vehicleSchedules[device.id]);

        if (!firstVehicleWithSchedule) return;

        const configuredScheduleName = vehicleSchedules[firstVehicleWithSchedule.id];

        const configuredSchedule = userSchedules.find(schedule => schedule.name === configuredScheduleName);

        if (configuredSchedule) {
            setSelectedSchedule(configuredSchedule);
        }

    }, [userSchedules, defaultEventRows, vehicleSchedules, devices]);

    useEffect(() => {
        if (userSchedules.length === 0 || devices.length === 0) return;

        let filteredVehicles = [];

        if (selectedVehicles.includes('all')) {
            filteredVehicles = devices;
        } else {
            filteredVehicles = devices.filter(device =>
                selectedVehicles.includes(device.id)
            );
        }

        const schedulesByVehicle = {};

        filteredVehicles.forEach(device => {
            const assignedSchedule = userSchedules.find(
                schedule => schedule.vehicleId === device.id
            );

            schedulesByVehicle[device.id] = assignedSchedule?.name || '';
        });

        setVehicleSchedules(schedulesByVehicle);

    }, [userSchedules, devices, selectedVehicles]);

    useEffect(() => {
        if (devices && devices.length > 0) {
            setSelectedVehicles(['all']); // Siempre todos al iniciar
        }
    }, [devices]);

    const handleTabChange = (event, newIndex) => {
        setTabIndex(newIndex);
    };

    const preprocessOrientations = (orientationsMap) => {
        const result = {};

        Object.entries(orientationsMap).forEach(([vehicleId, list]) => {
            result[vehicleId] = list
                .map(o => ({ ...o, seconds: timeToSeconds(o.time.split(' ')[1]) }))
                .sort((a, b) => a.seconds - b.seconds);
        });

        return result;
    };

    const normalizeLabel = (label) => {
        return label.replace(' Ida', '').replace(' Vuelta', '');
    }

    const getRoutePointsByCheckpoint = (checkpoint) => {
        const normalizedCheckpoint = normalizeLabel(checkpoint).toLowerCase().trim();

        return userRoute.filter(routePoint => {
            const normalizedRouteLabel = normalizeLabel(routePoint.label).toLowerCase().trim();

            return (
                routePoint.label.toLowerCase().trim() === checkpoint.toLowerCase().trim() ||
                normalizedRouteLabel === normalizedCheckpoint
            );
        });
    };

    const orderEventsByTracker = (events) => {
        // console.log(events)
        const routePoints = userRoute.map((point, index) => ({ index, id: String(point.id) }));
        const eventsByTracker = {};

        events.forEach(event => {
            if (!eventsByTracker[event.tracker_id]) {
                eventsByTracker[event.tracker_id] = [];
            }

            eventsByTracker[event.tracker_id].push(event);
        });

        const orderedEventsByTracker = {};

        Object.entries(eventsByTracker).forEach(([trackerId, trackerEvents]) => {
            const eventsSortedByTime = [...trackerEvents].sort((a, b) =>
                a.time.localeCompare(b.time)
            );

            let currentRouteIndex = -1;
            const orderedEvents = [];

            let invalidSequenceCount = 0;
            let lastValidEventTime = null;

            const MAX_INVALID_SEQUENCE = 5;
            const RESET_TIME_MINUTES = 30;

            eventsSortedByTime.forEach(event => {
                const zoneIds = (event.extra.zone_ids || []).map(String);

                const matchingIndexes = routePoints
                    .filter(point => zoneIds.includes(point.id))
                    .map(point => point.index);

                if (matchingIndexes.length === 0) {
                    return;
                }

                let nextIndex = -1;

                if (currentRouteIndex === -1) {
                    nextIndex = matchingIndexes[0];
                } else {
                    const MAX_TOLERANCE = 3;

                    const candidates = matchingIndexes
                        .map(index => {
                            let distance;

                            // Ruta normal hacia adelante
                            if (index >= currentRouteIndex) {
                                distance = index - currentRouteIndex;
                            }
                            // Wrap al inicio de la ruta
                            else {
                                const isNearEnd = currentRouteIndex >= userRoute.length - MAX_TOLERANCE;

                                // Wrap normal al final
                                if (isNearEnd) {
                                    distance = (userRoute.length - currentRouteIndex) + index;
                                }
                                // Permitir reinicio SOLO en Brisas (index 0)
                                else if (index === 0) {
                                    distance = 1;
                                }
                                // Retroceso inválido
                                else {
                                    distance = Infinity;
                                }
                            }

                            return { index, distance };
                        }).filter(candidate => candidate.distance !== Infinity)
                        .sort((a, b) => a.distance - b.distance);

                    // Permitir mismo checkpoint
                    const sameCheckpoint = candidates.find(
                        candidate => candidate.distance === 0
                    );

                    if (sameCheckpoint) {
                        nextIndex = sameCheckpoint.index;
                    } else {
                        // Solo permitir avanzar máximo 3 checkpoints
                        const validCandidate = candidates.find(candidate => {

                            return (
                                candidate.distance > 0 &&
                                candidate.distance <= MAX_TOLERANCE
                            );
                        });

                        if (validCandidate) {
                            nextIndex = validCandidate.index;
                        }
                    }
                }

                if (nextIndex === -1) {
                    invalidSequenceCount++;

                    const currentEventTime = dayjs(event.time);

                    const hasLongPause =
                        lastValidEventTime &&
                        currentEventTime.diff(lastValidEventTime, 'minute') >= RESET_TIME_MINUTES;

                    // Reiniciar seguimiento de ruta
                    if (
                        invalidSequenceCount >= MAX_INVALID_SEQUENCE ||
                        hasLongPause
                    ) {
                        // Buscar cualquier checkpoint válido para reiniciar
                        nextIndex = matchingIndexes[0];

                        invalidSequenceCount = 0;
                    }
                }

                if (nextIndex === -1) {
                    return;
                }

                currentRouteIndex = nextIndex;

                invalidSequenceCount = 0;
                lastValidEventTime = dayjs(event.time);

                orderedEvents.push({
                    ...event,
                    routeIndex: nextIndex
                });
            });

            orderedEventsByTracker[trackerId] = orderedEvents.sort((a, b) =>
                a.time.localeCompare(b.time)
            );
        });

        return orderedEventsByTracker;
    };

    const findClosestCheckpoint2 = (eventSeconds, schedule, zoneLabel, turnNumber, checkpointIndex) => {
        let bestMatch = null;
        let bestDifference = Infinity;
        let bestIsExact = false;

        const targetLabel = zoneLabel.toLowerCase().trim();
        const normalizedTargetLabel = normalizeLabel(zoneLabel).toLowerCase().trim();

        for (let i = turnNumber; i < schedule.turns.length; i++) {
            const startCheckpointIndex = i === turnNumber ? checkpointIndex : 0;

            for (let j = startCheckpointIndex; j < schedule.turns[i].length; j++) {
                const routePoint = schedule.turns[i][j];

                if (routePoint.time == null) {
                    continue;
                }

                const routePointLabel = routePoint.label.toLowerCase().trim();
                const normalizedRoutePointLabel = normalizeLabel(routePoint.label).toLowerCase().trim();
                const isExactMatch = routePointLabel === targetLabel;
                const isNormalizedMatch = normalizedRoutePointLabel === normalizedTargetLabel;

                if (!isExactMatch && !isNormalizedMatch) {
                    continue;
                }

                const difference = Math.abs(eventSeconds - routePoint.time);

                if (!bestMatch || (isExactMatch && !bestIsExact) || (isExactMatch === bestIsExact && difference < bestDifference)) {
                    bestMatch = { ...routePoint, checkpointIndex: j, turnNumber: i, difference };
                    bestDifference = difference;
                    bestIsExact = isExactMatch;
                }
            }
        }

        return bestMatch;
    };

    const handleGenerateReport = async () => {
        let vehicleIds = [];
        if (selectedVehicles.includes('all')) {
            vehicleIds = devices.map(d => d.id); // todos
        } else {
            vehicleIds = selectedVehicles;
        }

        if (!vehicleIds || vehicleIds.length === 0) {
            showAlert("Debe seleccionar al menos un vehículo para generar el reporte.", "error");
            setLoadReport(false);
            return;
        }

        setLoadReport(true);

        if (startDate && startTime && endDate && endTime) {
            const schedulesForReport = applyCompensationToReport
                ? compensatedSchedules
                : userSchedules;

            setReportSchedules(schedulesForReport);

            // Combinar fecha y hora para inicio y fin
            const combinedStartDateTime = startDate
                .hour(startTime.hour())
                .minute(startTime.minute())
                .second(startTime.second());

            const combinedEndDateTime = endDate
                .hour(endTime.hour())
                .minute(endTime.minute())
                .second(endTime.second());


            try {
                const startFormatted = combinedStartDateTime.format('YYYY-MM-DD HH:mm:ss');
                const endFormatted = combinedEndDateTime.format('YYYY-MM-DD HH:mm:ss');

                const reportData = await generateReport(startFormatted, endFormatted, vehicleIds);

                if (!reportData) {
                    setLoadReport(false);
                    return;
                }

                // Periodo para orientaciones (ej: mismo periodo del reporte)
                const orientationFrom = combinedStartDateTime.toISOString();
                const orientationTo = combinedEndDateTime.toISOString();

                // Generar orientaciones por vehículo
                const orientationsReport = await generateOrientationsReport(
                    orientationFrom,
                    orientationTo,
                    vehicleIds
                );

                // Mapear por vehicleId para acceso rápido
                const orientationsMap = {};

                orientationsReport.forEach(item => {
                    orientationsMap[item.vehicleId] = item.orientations;
                });

                const processedOrientations = preprocessOrientations(orientationsMap);

                const reportEvents = [];

                // Navegar por los datos de reportData
                reportData.report.sheets.forEach(sheet => {
                    sheet.sections.forEach(section => {
                        if (section.type === 'table' && section.header === 'Visitas de zona') {
                            getReportRows(section).forEach(({ row, dataItem }) => {
                                const exitCell =
                                    row.out_time
                                    || getReportCellByKeywords(row, section, ['outtime', 'exit', 'salida', 'end', 'fin', 'to'])
                                    || row.time;

                                const enterCell =
                                    row.in_time
                                    || getReportCellByKeywords(row, section, ['intime', 'enter', 'entrada', 'start', 'inicio', 'from']);

                                const zoneCell =
                                    row.zone_name
                                    || getReportCellByKeywords(row, section, ['zonename', 'geofence', 'geocerca', 'zone', 'zona'])
                                    || row.event
                                    || dataItem.header;

                                const rawTime = parseReportDate(exitCell) || parseReportDate(enterCell);
                                const checkpoint = cleanCheckpointLabel(zoneCell);
                                const zoneId = getReportCellRaw(zoneCell);

                                if (!rawTime || !checkpoint) return;

                                // Formatear el tiempo y la fecha
                                const timePart = rawTime.toLocaleTimeString('es-ES', { hour12: false }); // Ej: "06:39:14"
                                const datePart = rawTime.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); // Ej: "01-ene-2025"
                                const eventDate = `${timePart} - ${datePart}`;

                                const vehicleId = sheet.entity_ids[0];
                                const routePointsByZoneId = zoneId
                                    ? userRoute.filter(routePoint => String(routePoint.id) === String(zoneId))
                                    : [];
                                const relatedRoutePoints = routePointsByZoneId.length > 0
                                    ? routePointsByZoneId
                                    : getRoutePointsByCheckpoint(checkpoint);
                                const zoneIds = relatedRoutePoints.length > 0
                                    ? relatedRoutePoints.map(routePoint => routePoint.id)
                                    : [zoneId].filter(Boolean);

                                reportEvents.push({
                                    tracker_id: vehicleId,
                                    vehicle: sheet.header,
                                    time: dayjs(rawTime).format('YYYY-MM-DD HH:mm:ss'),
                                    timePart,
                                    eventDate,
                                    timeDate: rawTime,
                                    checkpointRaw: checkpoint,
                                    relatedRoutePoints,
                                    extra: {
                                        tracker_label: sheet.header,
                                        zone_ids: zoneIds,
                                        zone_labels: [checkpoint]
                                    }
                                });
                            });
                        }
                    });
                });

                const orderedEventsByTracker = orderEventsByTracker(reportEvents);
                // console.log(reportData)
                // console.log(orderedEventsByTracker)
                // console.log(userRoute)
                const processedRows = [];

                Object.values(orderedEventsByTracker).flat().forEach(event => {
                    const checkpoint = event.checkpointRaw;
                    const vehicleId = event.tracker_id;
                    // const schedule = userSchedules.find(schedule => schedule.vehicleId === vehicleId);
                    const selectedScheduleName = vehicleSchedules[vehicleId];

                    const schedule = schedulesForReport.find(
                        schedule => schedule.name === selectedScheduleName
                    );

                    if (!schedule) return;

                    const eventSeconds = timeToSeconds(event.timePart);
                    const relatedRoutePoints = event.relatedRoutePoints || getRoutePointsByCheckpoint(checkpoint);
                    const directionalRoutePoints = relatedRoutePoints.filter(isDirectionalRoutePoint);

                    let zoneLabel = checkpoint;

                    if (directionalRoutePoints.length > 0) {
                        let closestOrientation = null;
                        let smallestOrientationDiff = Infinity;

                        for (const orientation of processedOrientations[vehicleId] || []) {
                            const diff = Math.abs(orientation.seconds - eventSeconds);

                            if (diff < smallestOrientationDiff) {
                                smallestOrientationDiff = diff;
                                closestOrientation = orientation;
                            }
                        }

                        if (closestOrientation) {
                            let filteredDirectional = null;
                            let smallestAngleDiff = Infinity;

                            directionalRoutePoints.forEach(routePoint => {
                                if (routePoint.orientation == null) return;

                                const angleDiff = getAngleDifference(closestOrientation.heading, routePoint.orientation);

                                if (angleDiff < smallestAngleDiff) {
                                    smallestAngleDiff = angleDiff;
                                    filteredDirectional = routePoint;
                                }
                            });

                            if (filteredDirectional) {
                                zoneLabel = filteredDirectional.label;
                            }
                        } else if (event.routeIndex != null && userRoute[event.routeIndex]) {
                            zoneLabel = userRoute[event.routeIndex].label;
                        }
                    } else if (event.routeIndex != null && userRoute[event.routeIndex]) {
                        zoneLabel = userRoute[event.routeIndex].label;
                    } else if (relatedRoutePoints.length === 1) {
                        zoneLabel = relatedRoutePoints[0].label;
                    }

                    const closestCheckpoint = findClosestCheckpoint2(
                        eventSeconds,
                        schedule,
                        zoneLabel,
                        0, 0
                    );

                    let finalCheckpoint = checkpoint;
                    let status = '-';
                    let difference = '-';
                    let turnNumber = '-';

                    if (closestCheckpoint) {
                        finalCheckpoint = closestCheckpoint.finalLabel || closestCheckpoint.label || zoneLabel;
                        turnNumber = closestCheckpoint.turnNumber + 1;

                        const result = calculateTimeDifference(
                            event.timePart,
                            closestCheckpoint.time,
                            toleranceTime
                        );

                        status = result.status;
                        difference = result.difference;
                    }

                    processedRows.push({
                        vehicle: event.vehicle,
                        scheduleName: schedule.name,
                        checkpoint: finalCheckpoint,
                        time: event.eventDate,
                        timeDate: event.timeDate,
                        turnNumber,
                        checkpointIndex: closestCheckpoint?.checkpointIndex ?? null,
                        status,
                        difference,
                        routeIndex: event.routeIndex,
                        trackerId: vehicleId
                    });
                });
                // console.log(processedRows)
                processedRows.sort((a, b) => b.timeDate - a.timeDate);

                setDefaultEventRows(processedRows);

                setRows(processedRows);

                const firstVehicleId = vehicleIds[0];
                const firstScheduleName = vehicleSchedules[firstVehicleId];
                const firstSchedule = schedulesForReport.find(schedule => schedule.name === firstScheduleName);

                if (firstSchedule) {
                    const firstVehicle = devices.find(device => device.id === firstVehicleId);

                    setSelectedSchedule({
                        ...firstSchedule,
                        configuredVehicleId: firstVehicleId,
                        configuredVehicleLabel: firstVehicle?.label
                    });
                }
            } catch (error) {
                console.error("Error al generar el reporte:", error);
            }
        }
        setLoadReport(false);
    };

    const handleSort = (column) => {
        const newOrder = orderBy === column
            ? (order === 'init' ? 'asc' : (order === 'asc' ? 'desc' : 'init'))
            : 'asc';

        setOrder(newOrder);
        setOrderBy(column);

        if (newOrder === 'init') {
            if (rows.length === 0 || userSchedules.length === 0) return;

            setRows(defaultEventRows); // Restaura el orden original si el estado es 'init'
            return;
        }

        const isAsc = newOrder === 'asc';

        const sortedRows = [...rows].sort((a, b) => {
            const valueA = a[column];
            const valueB = b[column];

            // Manejo de valores undefined o null
            if (valueA === null || valueA === undefined || valueA === '-') return 1;
            if (valueB === null || valueB === undefined || valueB === '-') return -1;

            // Comparación según el tipo de datos
            if (typeof valueA === 'string' && typeof valueB === 'string') {
                return isAsc ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
            } else if (typeof valueA === 'number' && typeof valueB === 'number') {
                return isAsc ? valueA - valueB : valueB - valueA;
            }

            return 0; // Si no es string ni number, no se ordena
        });

        setRows(sortedRows);
    };

    const getSortIcon = (column) => {
        if (orderBy !== column || order === 'init') return <span>-</span>;
        return order === 'asc' ? (
            <span className='material-symbols-rounded'>arrow_upward_alt</span>
        ) : (
            <span className='material-symbols-rounded'>arrow_downward_alt</span>
        );
    };


    const getStatusStyle = (status) => {
        switch (status) {
            case 'Atrasado':
                return { backgroundColor: 'var(--color-danger)' };
            case 'En tiempo':
                return { backgroundColor: 'var(--color-success)' };
            case 'Adelantado':
                return { backgroundColor: 'var(--color-warning)' };
            default:
                return {};
        }
    };

    const selectStyles = {
        height: '35px', padding: '8px', fontSize: '0.65rem', borderRadius: '5px', color: 'var(--color-dark)', backgroundColor: 'var(--color-white)',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-dark-variant)', // Cambia el color del borde del estado normal
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-dark)', // Cambia el color al pasar el mouse
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--color-primary)', // Cambia el color al enfocar el campo
        },
        '& .MuiSelect-icon': {
            color: 'var(--color-dark)', // Cambia el color de la flecha aquí
        },
        '&:hover .MuiSelect-icon': {
            color: 'var(--color-dark)', // Cambia el color al pasar el mouse
        },
    }

    const tableCellHeaderStyle = { textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: '#ffff' };
    const tableCellBodyStyle = { textAlign: 'center', fontSize: '0.8rem', color: 'var(--color-dark)', border: '1px solid var(--color-dark-light)' };

    const handleVehiclesChange = (event) => {
        const value = event.target.value;
        if (value.includes('all')) {
            // Si selecciona "Todos", selecciona solo esa opción
            setSelectedVehicles(['all']);
        } else {
            // Si no hay ningún vehículo, o si selecciona todos manualmente, fuerza a 'all'
            if (value.length === devices.length) {
                setSelectedVehicles(['all']);
            } else {
                setSelectedVehicles(value);
            }
        }
    };

    const handleVehicleScheduleChange = (vehicleId, scheduleName) => {
        setVehicleSchedules(prev => ({ ...prev, [vehicleId]: scheduleName }));
    };

    return (
        <section className='reports_container'>
            {/* <h2 className='card_title'>Reportes</h2> */}
            {/* <div className='card_content' style={{ display: 'flex', flexDirection: 'column' }}> */}
            <div style={{ display: 'flex', marginTop: '1rem', gap: '1rem', flexWrap: 'wrap' }}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                    <DatePicker
                        label="Fecha de inicio"
                        defaultValue={dayjs().startOf('day')}
                        onChange={(newValue) => setStartDate(newValue.startOf('day'))}
                        sx={{
                            flex: 1,
                            minWidth: '170px',
                            '& .MuiInputLabel-root': {
                                color: 'var(--color-dark)',
                                // fontSize: '0.7rem' // Tamaño de la etiqueta
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'var(--color-dark-variant)' },
                                '&:hover fieldset': { borderColor: 'var(--color-dark)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--color-dark)' },
                            },
                            '& .MuiInputBase-input': {
                                color: 'var(--color-dark)',
                                fontSize: '0.8rem', // Tamaño del texto de entrada
                                padding: '1rem 12px'
                            },
                            '& .MuiSvgIcon-root': { color: 'var(--color-dark)' }, // Color del ícono
                        }}

                    />
                    <TimePicker
                        label="Hora de inicio"
                        defaultValue={dayjs().startOf('day')}
                        onChange={(newValue) => setStartTime(newValue)}
                        sx={{
                            flex: 1,
                            minWidth: '170px',
                            '& .MuiInputLabel-root': {
                                color: 'var(--color-dark)',
                                // fontSize: '0.7rem' // Tamaño de la etiqueta
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'var(--color-dark-variant)' },
                                '&:hover fieldset': { borderColor: 'var(--color-dark)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--color-dark)' },
                            },
                            '& .MuiInputBase-input': {
                                color: 'var(--color-dark)',
                                fontSize: '0.8rem', // Tamaño del texto de entrada
                                padding: '1rem 12px'
                            },
                            '& .MuiSvgIcon-root': { color: 'var(--color-dark)' }, // Color del ícono
                        }}

                    />
                    <DatePicker
                        label="Fecha de fin"
                        defaultValue={dayjs().endOf('day')}
                        onChange={(newValue) => setEndDate(newValue.startOf('day'))}
                        sx={{
                            flex: 1,
                            minWidth: '170px',
                            '& .MuiInputLabel-root': {
                                color: 'var(--color-dark)',
                                // fontSize: '0.7rem' // Tamaño de la etiqueta
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'var(--color-dark-variant)' },
                                '&:hover fieldset': { borderColor: 'var(--color-dark)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--color-dark)' },
                            },
                            '& .MuiInputBase-input': {
                                color: 'var(--color-dark)',
                                fontSize: '0.8rem', // Tamaño del texto de entrada
                                padding: '1rem 12px'
                            },
                            '& .MuiSvgIcon-root': { color: 'var(--color-dark)' }, // Color del ícono
                        }}

                    />
                    <TimePicker
                        label="Hora de fin"
                        defaultValue={dayjs().endOf('day')}
                        onChange={(newValue) => setEndTime(newValue)}
                        sx={{
                            flex: 1,
                            minWidth: '170px',
                            '& .MuiInputLabel-root': {
                                color: 'var(--color-dark)',
                                // fontSize: '0.7rem' // Tamaño de la etiqueta
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: 'var(--color-dark-variant)' },
                                '&:hover fieldset': { borderColor: 'var(--color-dark)' },
                                '&.Mui-focused fieldset': { borderColor: 'var(--color-dark)' },
                            },
                            '& .MuiInputBase-input': {
                                color: 'var(--color-dark)',
                                fontSize: '0.8rem', // Tamaño del texto de entrada
                                padding: '1rem 12px'
                            },
                            '& .MuiSvgIcon-root': { color: 'var(--color-dark)' }, // Color del ícono
                        }}

                    />
                </LocalizationProvider>
                <FormControl sx={{ minWidth: 200, flex: 1 }}>
                    <InputLabel id="vehicle-select-label" sx={{
                        color: 'var(--color-dark)',
                        fontSize: '0.8rem', // Tamaño de la etiqueta cuando NO está en foco
                        '&.MuiInputLabel-shrink': {
                            fontSize: '1rem', // Mantiene el mismo tamaño cuando está en foco
                        }
                    }}>Vehículos</InputLabel>
                    <Select
                        labelId="vehicle-select-label"
                        multiple
                        label="Vehículos"
                        value={selectedVehicles}
                        onChange={handleVehiclesChange}
                        renderValue={(selected) => {
                            if (selected.includes('all')) return 'Todos los vehículos';
                            const selectedNames = devices
                                .filter(dev => selected.includes(dev.id))
                                .map(dev => dev.label)
                                .join(', ');
                            return selectedNames || 'Ninguno';
                        }}
                        sx={{
                            minWidth: '200px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--color-dark-variant)', // Color del borde por defecto
                            },
                            '&:not(.Mui-focused):hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--color-dark)', // Color del borde al pasar el mouse
                            },
                            '& .MuiInputBase-input': {
                                color: 'var(--color-dark)',
                                fontSize: '0.8rem', // Tamaño del texto de entrada
                                padding: '0.85rem 12px'
                            },
                        }}
                        MenuProps={{
                            PaperProps: {
                                sx: {
                                    fontSize: '0.8rem',  // <--- Cambia el tamaño de fuente del menú
                                    '& .MuiCheckbox-root': {
                                        padding: '4px 8px'
                                    },
                                    '& .MuiListItemText-primary': {
                                        fontSize: '0.8rem', // <--- Cambia el tamaño del texto de las opciones
                                    }
                                }
                            }
                        }}
                    >
                        <MenuItem value="all">
                            <Checkbox checked={selectedVehicles.includes('all')} />
                            <ListItemText primary="Todos los vehículos" />
                        </MenuItem>
                        {devices.slice().sort(mixedNaturalSort).map(dev => (
                            <MenuItem
                                key={dev.id}
                                value={dev.id}
                                disabled={selectedVehicles.includes('all')}
                            >
                                <Checkbox checked={selectedVehicles.includes(dev.id) || selectedVehicles.includes('all')} />
                                <ListItemText primary={dev.label} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <BootstrapTooltip title="Configurar horarios">
                    <Button
                        variant="outlined"
                        onClick={() => setOpenScheduleModal(true)}
                        sx={{
                            margin: 'auto 0',
                            padding: '0.5rem',
                            fontSize: '0.7rem',
                            borderRadius: 'var(--border-radius-1)',
                            transition: 'all 0.3s ease',
                            minWidth: 'auto'
                        }}
                    >
                        <span className="material-symbols-rounded" style={{ verticalAlign: 'middle', fontSize: '1rem' }}>event_note</span>
                    </Button>
                </BootstrapTooltip>
                <BootstrapTooltip title="Aplicar la compensación de tiempo configurada a los horarios usados para generar el reporte.">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={applyCompensationToReport}
                                onChange={(event) => setApplyCompensationToReport(event.target.checked)}
                                size="small"
                            />
                        }
                        label="Compensación"
                        sx={{
                            margin: 'auto 0',
                            color: 'var(--color-dark)',
                            '& .MuiFormControlLabel-label': {
                                fontSize: '0.75rem'
                            }
                        }}
                    />
                </BootstrapTooltip>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateReport}
                    disabled={!startDate || !endDate || !startTime || !endTime || loadReport}
                    sx={{ margin: 'auto 0', fontSize: '0.7rem', flex: 1, minWidth: '170px', }}
                >
                    Generar Reporte
                </Button>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{
                loadReport ?
                    <Box sx={{ margin: 'auto 0', textAlign: 'center' }}>
                        <CircularProgress />
                    </Box>
                    :
                    rows.length > 0 ? (
                        <>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '1rem' }}>
                                <Tabs value={tabIndex} onChange={handleTabChange} >
                                    <Tab label="Por evento" sx={{
                                        color: 'var(--color-dark)', // Color para pestañas inactivas
                                        '&.Mui-selected': { color: 'var(--color-primary)' },
                                        fontSize: '0.7rem'
                                    }} />
                                    <Tab label="Por horario" sx={{
                                        color: 'var(--color-dark)', // Color para pestañas inactivas
                                        '&.Mui-selected': { color: 'var(--color-primary)' },
                                        fontSize: '0.7rem'
                                    }} />
                                    <Tab label="Estadísticas" sx={{
                                        color: 'var(--color-dark)', // Color para pestañas inactivas
                                        '&.Mui-selected': { color: 'var(--color-primary)' },
                                        fontSize: '0.7rem'
                                    }} />
                                </Tabs>
                            </Box>
                            <TabPanel value={tabIndex} index={0}>
                                <EventsTab
                                    index={0}
                                    devices={devices}
                                    userRoute={userRoute}
                                    rows={rows}
                                    selectStyles={selectStyles}
                                    tableCellHeaderStyle={tableCellHeaderStyle}
                                    tableCellBodyStyle={tableCellBodyStyle}
                                    vehicleFilter={vehicleFilter}
                                    setVehicleFilter={setVehicleFilter}
                                    checkpointFilter={checkpointFilter}
                                    setCheckpointFilter={setCheckpointFilter}
                                    statusFilter={statusFilter}
                                    setStatusFilter={setStatusFilter}
                                    handleSort={handleSort}
                                    getSortIcon={getSortIcon}
                                    getStatusStyle={getStatusStyle}
                                />
                            </TabPanel>
                            <TabPanel value={tabIndex} index={1}>
                                <ScheduleTab
                                    devices={devices}
                                    userSchedules={reportSchedules}
                                    vehicleSchedules={vehicleSchedules}
                                    selectedSchedule={selectedSchedule}
                                    setSelectedSchedule={setSelectedSchedule}
                                    defaultEventRows={defaultEventRows}
                                    selectStyles={selectStyles}
                                />
                            </TabPanel>
                            <TabPanel value={tabIndex} index={2}>
                                <StatsTab
                                    devices={devices}
                                    userRoute={userRoute}
                                    userSchedules={reportSchedules}
                                    rows={rows}
                                    vehicleFilter={vehicleFilter}
                                    setVehicleFilter={setVehicleFilter}
                                    checkpointFilter={checkpointFilter}
                                    setCheckpointFilter={setCheckpointFilter}
                                    statusFilter={statusFilter}
                                    setStatusFilter={setStatusFilter}
                                    selectStyles={selectStyles}
                                    tableCellHeaderStyle={tableCellHeaderStyle}
                                    tableCellBodyStyle={tableCellBodyStyle}
                                />
                            </TabPanel>
                        </>
                    ) : (
                        <div style={{ margin: 'auto 0', textAlign: 'center' }}>
                            <span style={{ fontStyle: 'italic' }}>Genere un reporte para visualizar los datos.</span>
                        </div>
                    )
            }
            </div>
            {/* </div> */}
            <Dialog
                open={openScheduleModal}
                onClose={() => setOpenScheduleModal(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: '0.5rem'
                    }
                }}
            >
                <DialogTitle>
                    Configurar horarios por vehículo
                </DialogTitle>

                <DialogContent
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.8rem',
                        paddingTop: '1rem !important',
                        paddingBottom: '1rem'
                    }}
                >
                    {(selectedVehicles.includes('all')
                        ? devices
                        : devices.filter(device =>
                            selectedVehicles.includes(device.id)
                        )
                    )
                        .slice()
                        .sort(mixedNaturalSort)
                        .map(vehicle => {

                            const schedules = userSchedules
                                .slice()
                                .sort((a, b) => a.name.localeCompare(b.name));

                            return (
                                <Box
                                    key={`vehicle-config-${vehicle.id}`}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        padding: '0.8rem 1rem',
                                        transition: '0.2s ease',
                                    }}
                                >

                                    {/* Vehículo */}
                                    <Box
                                        sx={{
                                            minWidth: '80px',
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: '0.7rem',
                                                marginTop: '-1rem',
                                            }}
                                        >
                                            Vehículo
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: '0.95rem',
                                                fontWeight: 600,
                                                color: 'var(--color-dark)'
                                            }}
                                        >
                                            {vehicle.label}
                                        </Typography>
                                    </Box>

                                    {/* Selector */}
                                    <FormControl
                                        fullWidth
                                        size="small"
                                    >
                                        <InputLabel>
                                            Horario
                                        </InputLabel>

                                        <Select
                                            value={
                                                vehicleSchedules[vehicle.id] ?? ''
                                            }
                                            label="Horario"
                                            onChange={(e) =>
                                                handleVehicleScheduleChange(
                                                    vehicle.id,
                                                    e.target.value
                                                )
                                            }
                                            sx={{
                                                borderRadius: '8px',
                                                backgroundColor: 'white',

                                                '& .MuiSelect-select': {
                                                    fontSize: '0.9rem'
                                                }
                                            }}
                                        >
                                            {schedules.map((schedule, index) => (
                                                <MenuItem
                                                    key={`schedule-option-${vehicle.id}-${schedule.name}-${index}`}
                                                    value={schedule.name}
                                                    sx={{
                                                        fontSize: '0.9rem'
                                                    }}
                                                >
                                                    {schedule.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            );
                        })}
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setOpenScheduleModal(false)}
                    >
                        Aceptar
                    </Button>
                </DialogActions>
            </Dialog>
        </section>
    )
}

export default Reports
